import { ChangeEvent, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, InputAdornment } from '@mui/material';
import { Grid } from '@mui/system';
import { FormDialog } from 'components';
import CustomTextField from 'components/CustomTextField';
import { KEEP_NOTES, VERSION_NAME_PREFIX } from 'pages/ValuationsAllocation/common/constants/valuations/valuations';

const MIN_VALUATION_VERSION_NAME_LENGTH = 3;

interface NewVersionFormValues {
  name: string;
  keep_notes: boolean;
}

interface NewVersionFormProps {
  nextVersionNumber: number;
  defaultValues: NewVersionFormValues;
  onClose: () => void;
  onSave: (values: NewVersionFormValues) => void;
  open: boolean;
}

const NewVersionForm = ({ 
  nextVersionNumber, 
  defaultValues, 
  onClose, 
  onSave, 
  open 
}: NewVersionFormProps) => {
  const [formValues, setFormValues] = useState<NewVersionFormValues>(defaultValues);

  const isValid = useMemo(() => {
    const trimmedName = formValues.name.trim();
    return trimmedName.length === 0 || trimmedName.length >= MIN_VALUATION_VERSION_NAME_LENGTH;
  }, [formValues.name]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    if (!isValid) { 
      return;
    }

    // Construct the final string only at the point of persistence
    const nickname = formValues.name.trim();
    const finalName = nickname 
      ? `Version ${nextVersionNumber} - ${nickname}` 
      : `Version ${nextVersionNumber}`;

    onSave({
      ...formValues,
      name: finalName
    });
  };

  return (
    <FormDialog
      open={open}
      title="Save as New Version"
      onClose={onClose}
      onSave={handleSave}
      isValid={isValid}
      editMode={false}
    >
      <Grid container spacing={2} sx={{ pt: 1 }}>
        <Grid size={12}>
          <CustomTextField
            fullWidth
            id="name"
            name="name"
            type="text" // Explicitly provided to satisfy CustomTextFieldProps
            label="Version Name"
            placeholder="Optional version name"
            value={formValues.name}
            onChange={handleChange}
            // Error handling for the minimum length requirement
            error={formValues.name.length < MIN_VALUATION_VERSION_NAME_LENGTH}
            helperText={
              formValues.name.length < MIN_VALUATION_VERSION_NAME_LENGTH
                ? `The version name must be at least ${MIN_VALUATION_VERSION_NAME_LENGTH} characters.`
                : ''
            }
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    {VERSION_NAME_PREFIX} {nextVersionNumber} -
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formValues.keep_notes}
                onChange={handleChange}
                name="keep_notes"
                color="primary"
              />
            }
            label={KEEP_NOTES}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default NewVersionForm;
